import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import MessagePanel from '../../common/components/MessagePanel/MessagePanel';
import { Provider } from '../../config';
import { EventPayload } from '../../events';
import { FlowConfigContext } from '../../providers/FlowConfigContext';
import { ScopeRequestContext } from '../../providers/ScopeRequestContext';
import withRouteAnalytics from '../../services/analytics/withRouteAnalytics';
import { Screens } from '../../services/analytics/screens';

function ChainErrorScreen(): JSX.Element {
  const { t } = useTranslation();
  const { getEventPayload: getSipEventPayload } = useContext(ScopeRequestContext);
  const { initialized, chainName, provider, flowFailed, getOptionalFlowExecutionConfig } =
    useContext(FlowConfigContext);

  const [chainDisplay, setChainDisplay] = useState<string>(t('chains.unknown'));
  const [errorCode, setErrorCode] = useState<string>('unknown');
  const location = useLocation();
  const navigate = useNavigate();
  const prevPath: string = location?.state?.prevPath || undefined;
  const locationErrorCode: string = location?.state?.errorCode || undefined;

  const handleConfirm = useCallback(() => {
    if (prevPath) {
      navigate(prevPath);
      return;
    }
    // If the current provider is SIP, then get the error payload form the SR context, which will include the SR id
    const eventPayload: EventPayload = provider === Provider.SIP ? getSipEventPayload() : { provider };
    flowFailed(eventPayload);
  }, [flowFailed, getSipEventPayload, navigate, prevPath, provider]);

  useEffect(() => {
    if (!initialized) {
      return;
    }

    // Fall back to displaying the unknown chain translation value if no translation is available for given chain variable
    const chain = t([`chains.${chainName}`, 'chains.unknown']);
    setChainDisplay(chain);
    const providedErrorCode = getOptionalFlowExecutionConfig('errorCode') || locationErrorCode;
    setErrorCode(providedErrorCode || 'unknown');
  }, [chainName, getOptionalFlowExecutionConfig, initialized, t, locationErrorCode]);

  const defaultErrorPath = `screens.chainError.errorCodes.${errorCode}`;
  const chainErrorPath = `screens.chainError.errorCodes.${chainName}.${errorCode}`;
  return (
    <>
      {chainName === 'solana' && (
        <MessagePanel
          title={[`${chainErrorPath}.title`, `${defaultErrorPath}.title`, `screens.chainError.title`]}
          // try to display an error message based on the provided error code, or fallback to the default
          description={[
            `${chainErrorPath}.description`,
            `${defaultErrorPath}.description`,
            `screens.chainError.solanaDefaultErrorMessage`,
            //    Placeholder for showing transaction ID
          ]}
          buttonLabel="screens.chainError.buttonLabel"
          translationVariables={{ chain: chainDisplay }}
          dataTestId="CHAIN_ERROR_SCREEN"
          onConfirm={handleConfirm}
          showSupportLink
        />
      )}
      {chainName !== 'solana' && (
        <MessagePanel
          title={[`${chainErrorPath}.title`, `${defaultErrorPath}.title`, `screens.chainError.title`]}
          // try to display an error message based on the provided error code, or fallback to the default
          description={[
            `${chainErrorPath}.description`,
            `${defaultErrorPath}.description`,
            `screens.chainError.evmDefaultErrorMessage`,
            //    Placeholder for showing transaction ID
          ]}
          buttonLabel="screens.chainError.buttonLabel"
          translationVariables={{ chain: chainDisplay }}
          dataTestId="CHAIN_ERROR_SCREEN"
          onConfirm={handleConfirm}
          showSupportLink
        />
      )}
    </>
  );
}
export default withRouteAnalytics(ChainErrorScreen, Screens.CHAIN_ERROR);
